import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, Clock, AlertCircle, TrendingUp, Calendar } from 'lucide-react';
import { Task } from '../../hooks/useTasks';
import { format, isToday, isFuture, isPast } from 'date-fns';

interface TaskStatsProps {
  tasks: Task[];
  onStatClick?: (filter: 'completed' | 'in_progress' | 'todo' | 'today' | 'overdue' | null) => void;
  activeFilter?: 'completed' | 'in_progress' | 'todo' | 'today' | 'overdue' | null;
}

const TaskStats: React.FC<TaskStatsProps> = ({ tasks, onStatClick, activeFilter }) => {
  const totalTasks = tasks.length;
  const completedTasks = tasks.filter(t => t.status === 'completed').length;
  const inProgressTasks = tasks.filter(t => t.status === 'in_progress').length;
  const todoTasks = tasks.filter(t => t.status === 'todo').length;
  
  const todayTasks = tasks.filter(t => {
    if (!t.due_date) return false;
    // Éviter les problèmes de timezone
    let taskDate: Date;
    if (t.due_date.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const [year, month, day] = t.due_date.split('-').map(Number);
      taskDate = new Date(year, month - 1, day);
    } else {
      taskDate = new Date(t.due_date);
    }
    return isToday(taskDate);
  }).length;

  const overdueTasks = tasks.filter(t => {
    if (!t.due_date || t.status === 'completed') return false;
    // Éviter les problèmes de timezone
    let taskDate: Date;
    if (t.due_date.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const [year, month, day] = t.due_date.split('-').map(Number);
      taskDate = new Date(year, month - 1, day);
    } else {
      taskDate = new Date(t.due_date);
    }
    return isPast(taskDate) && !isToday(taskDate);
  }).length;

  const completionRate = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;

  const stats = [
    {
      label: 'Total Tasks',
      value: totalTasks,
      icon: CheckCircle2,
      color: 'text-[rgb(var(--brand))]',
      bgColor: 'bg-blue-500/10',
      borderColor: 'border-blue-500/20',
      filter: null as const,
      clickable: true,
    },
    {
      label: 'Completed',
      value: completedTasks,
      icon: CheckCircle2,
      color: 'text-green-400',
      bgColor: 'bg-green-500/10',
      borderColor: 'border-green-500/20',
      filter: 'completed' as const,
      clickable: true,
    },
    {
      label: 'In Progress',
      value: inProgressTasks,
      icon: Clock,
      color: 'text-yellow-400',
      bgColor: 'bg-yellow-500/10',
      borderColor: 'border-yellow-500/20',
      filter: 'in_progress' as const,
      clickable: true,
    },
    {
      label: 'To Do',
      value: todoTasks,
      icon: AlertCircle,
      color: 'text-[rgb(var(--muted))]',
      bgColor: 'bg-gray-500/10',
      borderColor: 'border-gray-500/20',
      filter: 'todo' as const,
      clickable: true,
    },
    {
      label: 'Today',
      value: todayTasks,
      icon: Calendar,
      color: 'text-purple-400',
      bgColor: 'bg-purple-500/10',
      borderColor: 'border-purple-500/20',
      filter: 'today' as const,
      clickable: true,
    },
    {
      label: 'Overdue',
      value: overdueTasks,
      icon: AlertCircle,
      color: 'text-red-400',
      bgColor: 'bg-red-500/10',
      borderColor: 'border-red-500/20',
      filter: 'overdue' as const,
      clickable: true,
    },
    {
      label: 'Completion Rate',
      value: `${completionRate}%`,
      icon: TrendingUp,
      color: 'text-[#9c68f2]',
      bgColor: 'bg-[#9c68f2]/10',
      borderColor: 'border-[#9c68f2]/20',
      filter: null as const,
      clickable: false,
    },
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
      {stats.map((stat, index) => {
        const Icon = stat.icon;
        return (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.05 }}
            onClick={() => {
              if (stat.clickable && onStatClick) {
                if (stat.label === 'Total Tasks') {
                  // Pour Total Tasks, on réinitialise tous les filtres
                  onStatClick(null);
                } else {
                  onStatClick(stat.filter);
                }
              }
            }}
            className={`bg-[rgb(var(--card))] border rounded-2xl p-4 transition-all ${
              stat.clickable 
                ? `hover:border-[#9c68f2]/50 hover:bg-[rgb(var(--card))]/80 cursor-pointer active:scale-95 ${
                    activeFilter === stat.filter 
                      ? 'border-[#9c68f2] bg-[#9c68f2]/10 shadow-lg shadow-[#9c68f2]/20' 
                      : stat.borderColor
                  }`
                : stat.borderColor
            }`}
          >
            <div className="flex items-center justify-between mb-2">
              <div className={`${stat.bgColor} ${stat.color} p-2 rounded-lg`}>
                <Icon className="w-4 h-4" />
              </div>
            </div>
            <div className="mt-2">
              <p className={`text-2xl font-bold ${stat.color}`}>{stat.value}</p>
              <p className="text-xs text-[rgb(var(--muted))] mt-1">{stat.label}</p>
            </div>
          </motion.div>
        );
      })}
    </div>
  );
};

export default TaskStats;

